import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React, {useState} from 'react';

import {AbbreviationFormat, AMINO_ACID_ABBREVIATION_PREF_KEY} from 'biojs/Codons';
import {AaSequenceColorScheme} from 'molbio/aa-sequences/util/AaSequenceColoringConstants';
import {CodonOptimizationPreferences, Organism} from 'molbio/codon-optimization/CodonOptimizationConstants';
import OrganismSelector from 'molbio/OrganismSelector';
import {FONT_FAMILY_MONOSPACED, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {BORDER_MAJOR} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import usePreference from 'ui-library/hooks/usePreference';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

import {Codon, getCodonsForOrganism, getSourceForOrganism} from './CodonConstants';

/**
 * Group items in an array into sub-arrays of max length x.
 *
 * Ex. groupArray(['a', 'b', 'c', 'd', 'e'], 2) => [['a', 'b'], ['d', 'e'], ['f']]
 */
function groupArray<T>(arr: Array<T>, subGroupLength: number): Array<Array<T>> {
  const result: Array<Array<T>> = [];
  for (const [i, item] of arr.entries()) {
    const groupIndex = Math.floor(i / subGroupLength);
    result[groupIndex] = result[groupIndex] || [];
    result[groupIndex].push(item);
  }
  return result;
}

interface CodonCellProps {
  codon: Codon;
  selectCodon: (bases: string) => void;
  current: boolean;
  abbreviationFormat: AbbreviationFormat;
}

function CodonCell({codon, selectCodon, current, abbreviationFormat}: CodonCellProps): JSX.Element {
  const [hovering, setHovering] = useState(false);

  return (
    <tr
      key={codon.bases}
      className={classnames(`codon-bases-${codon.bases}`, css(styles.codonRow))}
      style={{backgroundColor: current || hovering ? codon.color : 'transparent'}}
      {...getTestProps({component: 'CodonTable', element: 'codonRow', key: codon.bases})}
      onClick={() => {
        selectCodon(codon.bases);
      }}
      onMouseOver={() => {
        setHovering(true);
      }}
      onMouseLeave={() => {
        setHovering(false);
      }}
      data-amp-safe={false}
    >
      <td
        className={classnames('bases', css(styles.codonBases, styles.monospaced))}
        {...getTestProps({component: 'CodonTable', element: 'bases', key: codon.bases})}
        data-amp-safe={false}
      >
        {codon.bases}
      </td>
      <td
        className={classnames('abbreviation', css(styles.abbreviation))}
        {...getTestProps({component: 'CodonTable', element: 'abbreviation', key: codon.bases})}
        data-amp-safe={false}
      >
        {codon[abbreviationFormat]}
      </td>
      <td
        className={classnames('fraction', css(styles.frequencyNumber, styles.monospaced))}
        {...getTestProps({component: 'CodonTable', element: 'fraction', key: codon.bases})}
        data-amp-safe={false}
      >
        {codon.fraction || '-'}
      </td>
      <td
        className={classnames('frequency', css(styles.frequencyNumber, styles.monospaced))}
        {...getTestProps({component: 'CodonTable', element: 'frequency', key: codon.bases})}
        data-amp-safe={false}
      >
        {codon.frequency || '-'}
      </td>
    </tr>
  );
}

interface CodonTableProps {
  selectedCodon: string;
  selectCodon: (bases: string) => void;
  colorScheme: AaSequenceColorScheme;
}

export function CodonTable(props: CodonTableProps): JSX.Element {
  const [selectedOrganism, setSelectedOrganism] = usePreference<Organism | null>(
    CodonOptimizationPreferences.MODAL_ORGANISM
  );
  const [abbreviationFormat, setAbbreviationFormat] = usePreference<AbbreviationFormat>(
    AMINO_ACID_ABBREVIATION_PREF_KEY
  );

  const setFormat = (e: InputChangeEvent): void => {
    const value = e.target.value as AbbreviationFormat;
    setAbbreviationFormat(value);
  };

  const codonRows = groupArray(groupArray(getCodonsForOrganism(selectedOrganism, props.colorScheme), 4), 4);
  const dataSource = selectedOrganism && getSourceForOrganism(selectedOrganism);

  return (
    <div className={css(styles.mainContainer)} {...getTestProps({component: 'CodonTable', element: 'root'})}>
      <div className="u-lr-split u-small-spacing-bottom">
        <label className="u-vertical-center-items">
          <span className="u-mini-spacing-right">Organism</span>
          <OrganismSelector
            className={css(styles.organismDropdown)}
            selected={selectedOrganism}
            onChange={setSelectedOrganism}
          />
        </label>
        <div>
          <label className="radio-inline radio-inline--labelAligned">
            <input
              type="radio"
              name="amino-acid-abbreviation"
              checked={abbreviationFormat === AbbreviationFormat.TRIPLE_LETTER}
              value={AbbreviationFormat.TRIPLE_LETTER}
              onChange={setFormat}
            />
            Three-letter
          </label>
          <label className="radio-inline radio-inline--labelAligned">
            <input
              type="radio"
              name="amino-acid-abbreviation"
              checked={abbreviationFormat === AbbreviationFormat.SINGLE_LETTER}
              value={AbbreviationFormat.SINGLE_LETTER}
              onChange={setFormat}
            />
            Single-letter
          </label>
        </div>
      </div>
      <table className={classnames('table-condensed', css(styles.outerTable))}>
        <tbody>
          {codonRows.map((row, i) => (
            <tr key={i}>
              {row.map((group, j) => (
                <td
                  className={css(
                    styles.innerTableCell,
                    i === 3 && styles.noBorderBottom,
                    j === 3 && styles.noBorderRight
                  )}
                  key={j}
                >
                  <table className={classnames('table-condensed', css(styles.innerTable))}>
                    {i === 0 && (
                      <thead>
                        <tr>
                          <th className={css(styles.codonBases)}>Codon</th>
                          <th className={css(styles.abbreviation)}>AA</th>
                          <th className={css(styles.frequencyNumber)}>Frac.</th>
                          <th className={css(styles.frequencyNumber)}>Freq.</th>
                        </tr>
                      </thead>
                    )}
                    <tbody>
                      {group.map((codon) => (
                        <CodonCell
                          key={codon.bases}
                          codon={codon}
                          current={props.selectedCodon === codon.bases}
                          selectCodon={props.selectCodon}
                          abbreviationFormat={abbreviationFormat}
                        />
                      ))}
                    </tbody>
                  </table>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <aside className="u-small-spacing-top">
        <span>
          <b>Frac.</b> = the abundance of this codon relative to all codons for this amino acid
        </span>
        <br />
        <span>
          <b>Freq.</b> = the average frequency of this codon's appearance per 1000 codons in the organism
        </span>
        <br />
        {dataSource && (
          <span>
            <b>Source:</b>{' '}
            <a target="_blank" rel="noopener noreferrer" href={dataSource}>
              {dataSource}
            </a>
          </span>
        )}
      </aside>
    </div>
  );
}

interface CodonTableModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (bases: string | null) => void;
  codon: string;
  colorScheme: AaSequenceColorScheme;
}

export default function CodonTableModal(props: CodonTableModalProps): JSX.Element {
  return (
    <Modal
      large={true}
      title="Change Amino Acid"
      onRequestClose={props.onRequestClose}
      buttons={[{text: 'Cancel', cancel: true}]}
    >
      <CodonTable
        selectedCodon={props.codon}
        selectCodon={props.onRequestSubmit}
        colorScheme={props.colorScheme}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    width: '700px',
    margin: 'auto',
  },
  organismDropdown: {
    width: '300px',
  },
  outerTable: {
    tableLayout: 'fixed',
  },
  innerTableCell: {
    borderRight: `1px solid ${BORDER_MAJOR}`,
    borderBottom: `1px solid ${BORDER_MAJOR}`,
    margin: 0,
    padding: 0,
  },
  innerTable: {
    width: '175px',
    tableLayout: 'fixed',
  },
  codonRow: {
    cursor: 'pointer',
    '.selected': {
      fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    },
  },
  codonBases: {
    width: '30%',
    textAlign: 'center',
  },
  abbreviation: {
    width: '20%',
  },
  frequencyNumber: {
    width: '25%',
  },
  monospaced: {
    fontFamily: FONT_FAMILY_MONOSPACED,
  },
  noBorderBottom: {
    borderBottom: 0,
  },
  noBorderRight: {
    borderRight: 0,
  },
});
